/**
 * Live refresh for Today (docs/ARCHITECTURE.md G5/G6).
 *
 * Reads sync through the API's shape proxy (GET /orgs/:orgId/shape/:table) with @electric-sql/client's ShapeStream.
 * When a synced row changes, the screen refetches its REST data, so every rule still runs in one place (@logwave/core,
 * FRAMEWORK T7). Electric is optional (FRAMEWORK T8): the first failed shape request (network error, 503 because
 * Electric is down, 404 because the route isn't there, any 4xx/5xx) stops the streams and switches to REST polling
 * every 10 seconds. The demo works with or without Electric.
 */
import type { Message, Row } from "@electric-sql/client";

/** Tables the API proxies (docs/API.md additions). */
export const SHAPE_TABLES = ["locations", "employees", "log_entries", "daily_checkins", "incidents", "review_items", "tasks"] as const;
export type ShapeTable = (typeof SHAPE_TABLES)[number];

export type SyncMode = "connecting" | "live" | "polling" | "stopped";
export interface SyncStatus {
  mode: SyncMode;
  /** When the data on screen was last refreshed (ms since epoch). */
  lastUpdated: number | null;
  /** Why we fell back to polling, in plain words (for a tooltip / logs). */
  reason?: string;
}

export interface LiveSyncOptions {
  tables: readonly ShapeTable[];
  /** Base URL of the shape proxy for this org, e.g. http://127.0.0.1:8787/orgs/o1/shape. Null: no live sync here (mock mode), poll. */
  shapeBase: string | null;
  /** Auth headers for the API (Bearer or x-dev-user). */
  headers?: () => Promise<Record<string, string>>;
  /** Refetch the screen's data. Called on a live change (debounced) and on every poll. */
  refresh: () => void | Promise<void>;
  onStatus?: (s: SyncStatus) => void;
  pollMs?: number;
  debounceMs?: number;
  /** Injected for tests; defaults to global fetch. */
  fetch?: typeof fetch;
  /** Extra "something changed" signal, e.g. the mock store's cross-tab storage event. */
  subscribeLocal?: (fn: () => void) => () => void;
}

export interface LiveSync { stop(): void; status(): SyncStatus }

export const POLL_MS = 10_000;

export function startLiveSync(o: LiveSyncOptions): LiveSync {
  const pollMs = o.pollMs ?? POLL_MS;
  const baseFetch = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const abort = new AbortController();
  let status: SyncStatus = { mode: "connecting", lastUpdated: Date.now() };
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  const unsubs: (() => void)[] = [];

  const setStatus = (next: Partial<SyncStatus>) => {
    status = { ...status, ...next };
    o.onStatus?.(status);
  };

  const refreshNow = async () => {
    if (stopped) return;
    try { await o.refresh(); setStatus({ lastUpdated: Date.now() }); } catch { /* the screen shows its own error state */ }
  };

  const scheduleRefresh = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => void refreshNow(), o.debounceMs ?? 250);
  };

  function fallBack(reason: string) {
    if (stopped || status.mode === "polling") return;
    abort.abort();
    for (const u of unsubs.splice(0)) { try { u(); } catch { /* ignore */ } }
    setStatus({ mode: "polling", reason });
    pollTimer = setInterval(() => void refreshNow(), pollMs);
    void refreshNow(); // catch up at once rather than waiting a full interval
  }

  if (o.subscribeLocal) unsubs.push(o.subscribeLocal(scheduleRefresh));

  if (!o.shapeBase) {
    fallBack("Live sync isn't available here");
  } else {
    // Every shape request goes through this guard, so a failure is detected on the first response instead of after the
    // client's own retries (which, for 5xx, back off indefinitely by default).
    const base = o.shapeBase;
    const guardedFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      let res: Response;
      try {
        res = await baseFetch(input, init);
      } catch (e) {
        // The client aborts its own requests (prefetches, tab hidden); only a real failure means "fall back".
        const aborted = abort.signal.aborted || init?.signal?.aborted || (e instanceof Error && e.name === "AbortError");
        if (!aborted) fallBack("Couldn't reach live sync");
        throw e;
      }
      // 409 is Electric's "shape rotated, refetch" signal, which the client handles itself.
      if (!res.ok && res.status !== 409) fallBack(res.status === 503 ? "Live sync is off (503)" : `Live sync answered ${res.status}`);
      return res;
    }) as typeof fetch;

    const synced = new Set<string>();
    void startStreams();
    async function startStreams() {
    let headers: Record<string, string> = {};
    try { headers = o.headers ? await o.headers() : {}; } catch { /* unauthenticated: the proxy will say so and we fall back */ }
    if (stopped) return;
    // Loaded here, not at start-up: the demo and polling mode never need it (134 KB off the first paint).
    const { ShapeStream, isChangeMessage, isControlMessage } = await import("@electric-sql/client");
    if (stopped) return;
    for (const table of o.tables) {
      try {
        const stream = new ShapeStream<Row>({
          url: `${base.replace(/\/+$/, "")}/${table}`,
          headers,
          fetchClient: guardedFetch,
          signal: abort.signal,
          backoffOptions: { initialDelay: 500, maxDelay: 5_000, multiplier: 2, maxRetries: 2 },
          onError: (e) => { fallBack(e instanceof Error ? e.message : "Live sync error"); },
        });
        unsubs.push(stream.subscribe((messages: Message<Row>[]) => {
          if (stopped || status.mode === "polling") return;
          const changed = messages.some((m) => isChangeMessage(m));
          const upToDate = messages.some((m) => isControlMessage(m) && m.headers.control === "up-to-date");
          // The first batch is the initial snapshot: the screen already has that data from REST.
          if (changed && synced.has(table)) scheduleRefresh();
          if (upToDate && !synced.has(table)) {
            synced.add(table);
            if (synced.size === o.tables.length) setStatus({ mode: "live", lastUpdated: Date.now() });
          }
        }, (e) => fallBack(e instanceof Error ? e.message : "Live sync error")));
      } catch (e) {
        fallBack(e instanceof Error ? e.message : "Live sync couldn't start");
        break;
      }
    }
    }
  }

  return {
    stop() {
      stopped = true;
      abort.abort();
      if (pollTimer) clearInterval(pollTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      for (const u of unsubs.splice(0)) { try { u(); } catch { /* ignore */ } }
      status = { ...status, mode: "stopped" };
    },
    status: () => status,
  };
}

/** "Updated just now" / "Updated 2 min ago" for the stale state (UX_SCOPE G16). */
export function updatedLabel(lastUpdated: number | null, now = Date.now()): string {
  if (!lastUpdated) return "Not updated yet";
  const s = Math.max(0, Math.round((now - lastUpdated) / 1000));
  if (s < 45) return "Updated just now";
  const m = Math.round(s / 60);
  if (m < 60) return `Updated ${m} min ago`;
  return `Updated ${Math.round(m / 60)} hr ago`;
}
